import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';

import { OutsideClickHandler } from '../../components';
import DashboardMenuItem from './DashboardMenuItem';

import css from './DashboardMenu.module.css';

const VARIANT_OVERFLOW = 'overflow';
const VARIANT_NAV = 'nav';
const VARIANT_BURGER = 'burger';
const VARIANT_QUIET = 'quiet';

let uid = 0;
const nextId = () => {
  uid += 1;
  return `dashboard-menu-${uid}`;
};

const isControlled = (isOpenProp, onToggleActiveProp) =>
  isOpenProp != null && onToggleActiveProp != null;

/**
 * A section heading inside a DashboardMenu. Presentational only: the keyboard
 * model skips it.
 */
export const DashboardMenuSection = props => (
  <div className={classNames(css.section, props.className)} aria-hidden="true">
    {props.title || props.children}
  </div>
);

/**
 * A rule between groups of rows. Presentational only.
 */
export const DashboardMenuDivider = props => (
  <div
    className={classNames(css.divider, props.className)}
    aria-hidden="true"
  />
);

const Chevron = () => (
  <svg
    className={css.chevron}
    width="12"
    height="8"
    viewBox="0 0 12 8"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M1 1l5 5 5-5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * One styled menu for every dashboard overflow and section switch.
 *
 * Wraps the platform primitives rather than the Menu component: Menu keeps its
 * content mounted at visibility:hidden, which leaves closed rows in the
 * accessibility tree. This panel unmounts instead. OutsideClickHandler and the
 * controlled isOpen/onToggleActive contract are unchanged.
 *
 * Presentation is chosen by --viewportMedium in CSS, not by a JS breakpoint, so
 * the server and the first client render agree. Above it the panel is an
 * absolutely positioned popover; below it the panel is full bleed, anchored to
 * the bottom of the trigger's container, with a scrim.
 *
 * @component
 * @param {Object} props
 * @param {('overflow'|'nav'|'burger'|'quiet')} props.variant - trigger style
 * @param {string} props.ariaLabel - required; names the menu
 * @param {ReactNode?} props.label - nav/burger: the current selection, shown in the bar
 * @param {string?} props.eyebrow - burger: small label above the current selection
 * @param {string?} props.title - optional panel header
 * @param {string?} props.subtitle - optional panel subhead
 * @param {('left'|'right')} props.align - desktop popover alignment, default right
 * @param {boolean?} props.isOpen - controlled open state; pair with onToggleActive
 * @param {Function?} props.onToggleActive - controlled toggle; pair with isOpen
 * @param {ReactNode} props.children - DashboardMenuItem, Section and Divider
 * @returns {JSX.Element} menu
 */
const DashboardMenu = props => {
  const {
    variant = VARIANT_OVERFLOW,
    ariaLabel,
    label,
    eyebrow,
    title,
    subtitle,
    align = 'right',
    className,
    rootClassName,
    contentClassName,
    triggerClassName,
    children,
    isOpen: isOpenProp = null,
    onToggleActive = null,
    id,
  } = props;

  const controlled = isControlled(isOpenProp, onToggleActive);
  if (!controlled && !(isOpenProp == null && onToggleActive == null)) {
    throw new Error(
      `DashboardMenu has invalid props:
        Both isOpen and onToggleActive need to be defined (controlled menu),
        or neither of them (menu uses its own state management).`
    );
  }

  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpen = controlled ? isOpenProp : uncontrolledOpen;

  const [activeIndex, setActiveIndex] = useState(0);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const menuIdRef = useRef(id || null);
  if (!menuIdRef.current) {
    menuIdRef.current = nextId();
  }
  const panelId = menuIdRef.current;

  const setOpen = useCallback(
    next => {
      if (controlled) {
        onToggleActive(next);
      } else {
        setUncontrolledOpen(next);
      }
    },
    [controlled, onToggleActive]
  );

  const close = useCallback(
    (returnFocus = true) => {
      setOpen(false);
      if (returnFocus && triggerRef.current) {
        triggerRef.current.focus();
      }
    },
    [setOpen]
  );

  // Positions of enabled rows, in render order. Sections, dividers and
  // disabled rows are not reachable by the arrow keys.
  const childArray = useMemo(() => React.Children.toArray(children), [
    children,
  ]);
  const enabled = useMemo(
    () =>
      childArray
        .map((child, i) => ({ child, i }))
        .filter(
          ({ child }) =>
            React.isValidElement(child) &&
            child.type === DashboardMenuItem &&
            !child.props.disabled
        )
        .map(({ i }) => i),
    [childArray]
  );

  const selectedEnabledIndex = useMemo(() => {
    const found = enabled.findIndex(i => childArray[i].props.selected);
    return found >= 0 ? found : 0;
  }, [enabled, childArray]);

  /**
   * The reachable rows, read from the panel rather than from a ref array.
   * Querying the committed DOM keeps this correct no matter how the children
   * are nested or re-ordered, and disabled rows drop out by the same attribute
   * that makes them unreachable to assistive tech.
   */
  const reachableRows = useCallback(
    () =>
      panelRef.current
        ? Array.from(
            panelRef.current.querySelectorAll(
              '[role="menuitem"]:not([aria-disabled="true"])'
            )
          )
        : [],
    []
  );

  const moveTo = useCallback(
    next => {
      const rowNodes = reachableRows();
      if (!rowNodes.length) {
        return;
      }
      const wrapped = (next + rowNodes.length) % rowNodes.length;
      setActiveIndex(wrapped);
      rowNodes[wrapped].focus();
    },
    [reachableRows]
  );

  // Opening focuses the selected row, or the first enabled one.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setActiveIndex(selectedEnabledIndex);
    const rowNodes = reachableRows();
    const node = rowNodes[selectedEnabledIndex];
    if (node) {
      node.focus();
    }
  }, [isOpen, selectedEnabledIndex, reachableRows]);

  const onPanelKeyDown = useCallback(
    e => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          moveTo(activeIndex + 1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          moveTo(activeIndex - 1);
          break;
        case 'Home':
          e.preventDefault();
          moveTo(0);
          break;
        case 'End':
          e.preventDefault();
          moveTo(enabled.length - 1);
          break;
        case 'Escape':
          e.preventDefault();
          close(true);
          break;
        case 'Tab':
          // Close and let focus continue to the next control on the page.
          close(false);
          break;
        default:
          break;
      }
    },
    [activeIndex, enabled.length, moveTo, close]
  );

  const onTriggerKeyDown = useCallback(
    e => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!isOpen) {
          setOpen(true);
        } else {
          moveTo(0);
        }
      } else if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        close(true);
      }
    },
    [isOpen, setOpen, moveTo, close]
  );

  let enabledCursor = 0;
  const rows = childArray.map((child, i) => {
    if (!React.isValidElement(child) || child.type !== DashboardMenuItem) {
      return child;
    }
    if (child.props.disabled) {
      return React.cloneElement(child, {
        key: child.key ?? i,
        onKeyDown: onPanelKeyDown,
      });
    }
    const myIndex = enabledCursor;
    enabledCursor += 1;
    // Roving tabindex: exactly one row is in the tab order at a time, so the
    // whole menu is a single tab stop.
    return React.cloneElement(child, {
      key: child.key ?? i,
      tabIndex: myIndex === activeIndex ? 0 : -1,
      onKeyDown: onPanelKeyDown,
      closeMenu: close,
    });
  });

  const triggerClasses = classNames(
    css.trigger,
    {
      [css.overflow]: variant === VARIANT_OVERFLOW,
      [css.nav]: variant === VARIANT_NAV,
      [css.burger]: variant === VARIANT_BURGER,
      [css.quiet]: variant === VARIANT_QUIET,
    },
    triggerClassName
  );

  const triggerContent =
    variant === VARIANT_OVERFLOW ? (
      <>
        <span className={css.dot} />
        <span className={css.dot} />
        <span className={css.dot} />
      </>
    ) : variant === VARIANT_BURGER ? (
      <>
        <span className={css.burgerLine} />
        <span className={css.burgerLine} />
        <span className={css.burgerLine} />
      </>
    ) : (
      <>
        <span className={css.triggerLabel}>{label}</span>
        <Chevron />
      </>
    );

  // The burger is one control; the current section renders beside it as text,
  // not as a second target.
  const burgerLabel =
    variant === VARIANT_BURGER && (label || eyebrow) ? (
      <span className={css.burgerText} aria-hidden="true">
        {eyebrow ? <span className={css.burgerEyebrow}>{eyebrow}</span> : null}
        <span className={css.burgerValue}>{label}</span>
      </span>
    ) : null;

  return (
    <OutsideClickHandler
      // rootClassName, not className: OutsideClickHandler's own root is
      // display:inline-block, which breaks the bar layout and the popover anchor.
      rootClassName={classNames(
        rootClassName || css.root,
        { [css.rootOverflow]: variant === VARIANT_OVERFLOW },
        className
      )}
      onOutsideClick={() => {
        if (isOpen) {
          close(false);
        }
      }}
    >
      <button
        type="button"
        ref={triggerRef}
        className={triggerClasses}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={panelId}
        aria-label={ariaLabel}
        onClick={() => (isOpen ? close(false) : setOpen(true))}
        onKeyDown={onTriggerKeyDown}
      >
        {triggerContent}
      </button>
      {burgerLabel}

      {isOpen ? (
        <>
          <div
            className={css.scrim}
            onClick={() => close(false)}
            aria-hidden="true"
          />
          <div
            id={panelId}
            ref={panelRef}
            role="menu"
            aria-label={ariaLabel}
            className={classNames(
              css.panel,
              align === 'left' ? css.alignLeft : css.alignRight,
              contentClassName
            )}
          >
            {title || subtitle ? (
              <div className={css.header}>
                {title ? <p className={css.headerTitle}>{title}</p> : null}
                {subtitle ? (
                  <p className={css.headerSubtitle}>{subtitle}</p>
                ) : null}
              </div>
            ) : null}
            <div className={css.list}>{rows}</div>
          </div>
        </>
      ) : null}
    </OutsideClickHandler>
  );
};

export default DashboardMenu;
